import React, { useEffect, useState } from "react";
import { api } from "../../../software/api";
import { X, Search } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const gold = {
  backgroundColor: "#c8a12a",
  backgroundImage:
    "linear-gradient(135deg,#b8901f 0%,#e9d074 22%,#caa42c 48%,#e9d074 74%,#b8901f 100%)",
};
export const money = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
export const todayISO = () => new Date().toLocaleDateString("en-CA");
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU") : "");

export const STATUS = [
  ["wo_quotation", "Work Order Quotation"],
  ["booked", "Work Order Booked"],
  ["in_progress", "In Progress"],
  ["completed", "Work Order Completed"],
  ["invoiced", "Invoiced"],
  ["paid", "Paid"],
  ["counter_sale", "Client Counter Sale"],
  ["cancelled", "Cancelled"],
];
export const statusLabel = (s) => (STATUS.find((x) => x[0] === s) || [s, s])[1];
export const noteSubject = (text) => {
  const first = () => String(text || "").split(/\r?\n/)[0].trim();
  let s = first();
  s = s.replace(/^\d{1,2}\/\d{1,2}\/\d{2,4}\s*/, "");        // leading date d/m/yy(yy)
  s = s.replace(/^[-–]\s*/, "");                              // separator
  s = s.replace(/^\d{1,2}:\d{2}(:\d{2})?\s*(am|pm)?\s*/i, ""); // leading time
  s = s.replace(/^[-–]\s*/, "");                              // separator
  return s.trim() || first();
};
export const isLockedStatus = (s) => {
  const v = String(s || "").trim().toLowerCase();
  if (!v) return false;
  return ["counter_sale", "client counter sale", "counter sale"].includes(v);
};
export const FALLBACK_BRANDS = ["Glamour Pools and Spas", "Just Spas Newcastle"];

export const KIND_ROW = {
  part: "bg-white",
  labour: "bg-[#eaf3ff]",
  manual: "bg-[#faf6ea]",
  supplier_freight: "bg-[#fffbdd]",
  store_credit: "bg-[#fdeaea]",
};
export const KIND_TAG = { part: null, labour: "LAB", manual: "MAN", supplier_freight: "FRT", store_credit: "CR" };

export const cellInp =
  "w-full min-w-0 bg-white border border-[#b9b9b9] px-1.5 py-1 text-[12px] text-slate-800 outline-none focus:border-[#4f8fd6]";

// Access "$#,##0.00" money box: formatted when idle, raw while editing, commits on blur/Enter (AfterUpdate).
export function MoneyInput({ value, onCommit, className = "", testid }) {
  const [txt, setTxt] = React.useState(null);
  const commit = () => { if (txt == null) return; const n = Math.round((parseFloat(String(txt).replace(/[$,\s]/g, "")) || 0) * 100) / 100; setTxt(null); if (n !== Number(value || 0)) onCommit(n); };
  return (
    <input inputMode="decimal" value={txt ?? money(value)} data-testid={testid} className={className}
      onFocus={(e) => { setTxt((Number(value) || 0).toFixed(2)); setTimeout(() => e.target.select(), 0); }}
      onChange={(e) => setTxt(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
  );
}

export const HdrBtn = ({ children, onClick, testid, className = "", size = "sm" }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`${size === "lg" ? "px-3 py-2 text-[12px] min-h-[38px]" : "px-2 py-1 text-[11px] min-h-[32px]"} inline-flex items-center justify-center text-center leading-tight font-bold rounded-sm border border-[#7ba7d9] bg-gradient-to-b from-white to-[#d7e6f7] text-[#1a4f8a] shadow-sm hover:from-[#eaf3ff] hover:to-[#c3daf3] transition-colors ${className}`}
  >
    {children}
  </button>
);

export const Field = ({ label, children }) => (
  <div className="flex items-center gap-2 text-[12px]">
    <span className="w-32 text-right font-bold text-[#123a63]">{label}</span>
    {children}
  </div>
);

export function ProductSearch({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      const qs = [];
      if (q) qs.push(`q=${encodeURIComponent(q)}`, "limit=100");
      if (cat) qs.push(`category=${encodeURIComponent(cat)}`);
      api("GET", `/products${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => live && setRows(r)).catch(() => {});
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, cat]);
  const CATS = [["", "All"], ["Spa", "Spas"], ["Swim Spa", "Swim Spas"], ["Gazebo", "Gazebos"], ["Sauna", "Saunas"]];
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-6" data-testid="wo-product-search">
      <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl mt-16 overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b bg-slate-50">
          <Search className="h-4 w-4 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="flex-1 text-sm outline-none bg-transparent" data-testid="wo-product-search-input" />
          <button onClick={onClose}><X className="h-4 w-4 text-slate-400" /></button>
        </div>
        <div className="flex gap-1 px-3 py-2 border-b flex-wrap">
          {CATS.map(([v, l]) => (
            <button key={v} onClick={() => setCat(v)} data-testid={`wo-product-cat-${v || "all"}`} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${cat === v ? "bg-[#123a63] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="max-h-80 overflow-y-auto divide-y">
          {rows.length === 0 && <p className="p-4 text-sm text-slate-400">No products.</p>}
          {rows.map((p) => (
            <button key={p.id} onClick={() => onPick(p)} data-testid="wo-product-option" className="w-full flex justify-between items-center px-4 py-2.5 text-left hover:bg-sky-50 text-sm">
              <span className="text-slate-800 flex items-center gap-2">
                {p.image_url && <img src={p.image_url} alt="" className="h-7 w-7 rounded object-cover border border-slate-200" />}
                <span>{p.name} <span className="text-slate-400 text-xs">{p.sku}</span></span>
              </span>
              <span className="font-mono2 text-slate-500">{money(p.price)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
